# 2023 Job Posting revision

The SEEK API was initially launched in our Australia & New Zealand markets.
It has since been enabled in [additional markets] across the Asia Pacific (APAC):

| Code | Name            | Brand       |
| :--: | :-------------- | :---------- |
|  HK  | Hong Kong       | [Jobsdb]    |
|  ID  | Indonesia       | [Jobstreet] |
|  MY  | Malaysia        | Jobstreet   |
|  PH  | The Philippines | Jobstreet   |
|  SG  | Singapore       | Jobstreet   |
|  TH  | Thailand        | Jobsdb      |

This guide details the process of revising an existing SEEK API integration built for the Australia & New Zealand markets for our expansion across the Asia Pacific.

## SEEK API only

This section is applicable to partners that are only integrated with the SEEK API for the Australia & New Zealand markets,
and did not previously support posting to our Jobstreet & Jobsdb job boards.

You will be able to expand your offering across the Asia Pacific upon completion of the revision.
The revision is required even if you do not offer your recruitment software in these additional markets,
as it includes changes to marketplace features that affect Australia & New Zealand hirers.

```mermaid
---
overrides:
  gantt:
    useWidth: 784
---
gantt
axisFormat   %b %Y
dateFormat   YYYY-MM-DD
tickInterval 3month
todayMarker off

section SEEK
Prepare revision :                                done, 2024-01-01, 2024-02-15
Release revision :                 done, milestone, m1, 2024-02-15,         0d
Provide support :                           active, s2,   after m1, 2024-12-31
Decommission features :            crit, milestone, m4, 2025-01-01,         0d

section Partner
Revise integration :                        active, p2,   after m1, 2024-12-31
```

Partners that are only integrated with the SEEK API have two development phases to complete:

1. [Developer Dashboard](/migration-guides/jobstreet-and-jobsdb-uplift/phase-1-developer-dashboard)
2. [Job Posting](/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting)

## SEEK API with existing Jobstreet & Jobsdb integrations

This section is applicable to partners that are integrated with the SEEK API for the Australia & New Zealand markets,
but also previously supported posting to our Jobstreet & Jobsdb job boards via existing integrations with APOS or Adloader.

You will be able to consolidate your onboarding processes, support model and technology upon completion of the revision.

```mermaid
---
overrides:
  gantt:
    useWidth: 784
---
gantt
axisFormat   %b %Y
dateFormat   YYYY-MM-DD
tickInterval 3month
todayMarker off

section SEEK
Distribute documentation :         done, milestone, m1, 2023-04-02,         0d
Productionise API :                         active, s1,   after m1, 2023-07-01
Support hirers & partners :                 active, s2,   after s1, 2024-01-20
Consolidate Jobstreet PH :               milestone, m2, 2023-10-14,         2d
Operate Jobstreet bridge :                  active, s3,   after m2, 2024-01-20
Consolidate rest of Jobstreet :          milestone, m3, 2023-11-11,         2d
Consolidate Jobsdb :               crit, milestone, m4, 2024-01-20,         2d

section Partner
Scope uplift :                              active, p1,   after m1, 2023-07-01
Build & test :                              active, p2,   after p1, 2023-11-01
Release to Australia & New Zealand hirers : active, p3, 2023-08-01, 2024-01-20
Reconcile Jobstreet & Jobsdb permissions :  active, p4, 2023-12-01, 2024-01-20
```

Partners with existing Jobstreet & Jobsdb integrations have four development phases to complete:

1. [Developer Dashboard](/migration-guides/jobstreet-and-jobsdb-uplift/phase-1-developer-dashboard)
2. [Job Posting](/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting)
3. [Apply & Talent Search Connect](/migration-guides/jobstreet-and-jobsdb-uplift/phase-3-apply-and-proactive-sourcing)
4. [Transitioning hirers](/migration-guides/jobstreet-and-jobsdb-uplift/phase-4-transitioning-hirers)

[additional markets]: /introduction#multi-market-support
[Jobsdb]: https://www.jobsdb.com/
[Jobstreet]: https://www.jobstreet.com/
